import {
  doc,
  getDoc,
  updateDoc,
  arrayRemove,
  arrayUnion,
} from 'firebase/firestore';
import { firestore } from '../services/firebase';

async function removeEntityFromFilter({ filterId, entityType, categoryId, cardId }) {
  const filterRef = doc(firestore, 'filters', filterId);
  const snap = await getDoc(filterRef);
  if (!snap.exists()) return;

  const data = snap.data();

  if (entityType === 'category') {
    const categoryIds = (data.categoryIds || []).filter((id) => id !== categoryId);
    await updateDoc(filterRef, { categoryIds });
    return;
  }

  const cardEntries = (data.cardEntries || []).filter(
    (entry) => !(entry.categoryId === categoryId && entry.cardId === cardId)
  );
  await updateDoc(filterRef, { cardEntries });
}

async function addEntityToFilter({ filterId, entityType, categoryId, cardId }) {
  const filterRef = doc(firestore, 'filters', filterId);
  const snap = await getDoc(filterRef);
  if (!snap.exists()) return;

  const data = snap.data();

  if (entityType === 'category') {
    const categoryIds = [...new Set([...(data.categoryIds || []), categoryId])];
    await updateDoc(filterRef, { categoryIds });
    return;
  }

  const alreadyLinked = (data.cardEntries || []).some(
    (entry) => entry.categoryId === categoryId && entry.cardId === cardId
  );
  if (alreadyLinked) return;

  await updateDoc(filterRef, {
    cardEntries: [...(data.cardEntries || []), { categoryId, cardId }],
  });
}

/**
 * Mantém paridade bidirecional com o painel de Filtros:
 * `filterIds` na entidade + `categoryIds` / `cardEntries` no doc do filtro.
 */
export async function syncEntityFilterLinks({
  entityType,
  categoryId,
  cardId,
  previousSlugs = [],
  nextSlugs = [],
  filtersBySlug,
}) {
  if (!categoryId || !filtersBySlug) return;

  const prev = new Set(previousSlugs.filter(Boolean));
  const next = new Set(nextSlugs.filter(Boolean));
  const toAdd = [...next].filter((slug) => !prev.has(slug));
  const toRemove = [...prev].filter((slug) => !next.has(slug));

  if (toAdd.length === 0 && toRemove.length === 0) return;

  const filterOps = [];

  for (const slug of toRemove) {
    const meta = filtersBySlug.get(slug);
    if (!meta?.id) continue;
    filterOps.push(
      removeEntityFromFilter({
        filterId: meta.id,
        entityType,
        categoryId,
        cardId,
      })
    );
  }

  for (const slug of toAdd) {
    const meta = filtersBySlug.get(slug);
    if (!meta?.id) continue;
    filterOps.push(
      addEntityToFilter({
        filterId: meta.id,
        entityType,
        categoryId,
        cardId,
      })
    );
  }

  await Promise.all(filterOps);

  const entityRef =
    entityType === 'category'
      ? doc(firestore, 'categories', categoryId)
      : doc(firestore, 'categories', categoryId, 'cards', cardId);

  const entityOps = [
    ...toRemove.map((slug) => updateDoc(entityRef, { filterIds: arrayRemove(slug) })),
    ...toAdd.map((slug) => updateDoc(entityRef, { filterIds: arrayUnion(slug) })),
  ];

  await Promise.all(entityOps);
}

export function buildFiltersBySlug(filters = []) {
  return new Map(filters.map((filter) => [filter.slug, filter]));
}

/** Slugs diretos do módulo (exclui os herdados da categoria). */
export function getDirectCardFilterSlugs(cardFilterIds = [], categoryFilterIds = []) {
  const inherited = new Set(categoryFilterIds || []);
  return (cardFilterIds || []).filter((slug) => !inherited.has(slug));
}
